/**
 * dealPreview.ts — the New Deal sheet's preview lines (slice 4b, RULED
 * 2026-10-07), pure, in their own module so SalesPipeline.tsx keeps
 * exporting components only and each rule is testable alone.
 *
 * The preview is GET /v1/sales/deal-preview (PR 641). Every line here is a
 * SOFT prompt: none of them gates Create or any validation. A null field
 * renders nothing — never a 0 or a guess standing in for it.
 */
import type { SalesDealPreview } from '../../../../api/types'
import { parsePoundsToPence } from '../../../../lib/money'
import { fmtDate, type DealPreviewQuery } from '../../api'
import { forecastCategoryLabel } from './vocab'

/** How long the sheet waits after the last edit before asking. */
export const DEAL_PREVIEW_DEBOUNCE_MS = 400

/** The read the sheet would make for this draft, or null for none: only
 *  when the value is non-empty AND parses — the wire takes the create's
 *  decimal pounds, and text that is not an amount has no such form. */
export function dealPreviewQuery(
  value: string, currency: string, stageSlug: string | undefined, expectedClose: string,
): DealPreviewQuery | null {
  if (value.trim() === '') return null
  const pence = parsePoundsToPence(value)
  if (pence === null) return null
  return {
    value: pence / 100,
    currency,
    ...(stageSlug ? { stage: stageSlug } : {}),
    ...(expectedClose ? { expectedClose } : {}),
  }
}

export interface DealPreviewLines {
  /** Non-GBP with a rate: the GBP-comparable figure and the fixing's date. */
  converted: string | null
  /** Non-GBP with no acceptable rate: the create would refuse this currency. */
  noRate: string | null
  /** The server's valueHint, in words. */
  hint: string | null
  /** The server's suggestion, its reason verbatim. */
  suggestion: string | null
}

const NONE: DealPreviewLines = { converted: null, noRate: null, hint: null, suggestion: null }

/** The lines a preview answers with. `money` is the sheet's own compact
 *  formatter (fmtCompact), passed in so this module imports no component. */
export function dealPreviewLines(p: SalesDealPreview | null, money: (pence: number) => string): DealPreviewLines {
  if (!p) return NONE
  const foreign = p.currency !== 'GBP'
  return {
    converted: foreign && p.basePence !== null && p.fxRateDate !== null
      ? `≈ ${money(p.basePence)} at the ECB rate of ${fmtDate(`${p.fxRateDate}T12:00:00Z`)}`
      : null,
    noRate: foreign && p.basePence === null
      ? `No GBP rate for ${p.currency} in the last 3 days — this deal can't be saved in ${p.currency} until the next daily fixing.`
      : null,
    hint: p.valueHint === 'over_five_times_largest_open'
      ? "That's more than five times your largest open deal — double-check the zeros."
      : p.valueHint === 'zero'
        ? 'A £0 deal adds nothing to forecast totals.'
        : null,
    suggestion: p.suggestion && p.suggestion.category !== '' && p.suggestion.reason.trim() !== ''
      ? `✦ Suggested forecast category: ${forecastCategoryLabel(p.suggestion.category)} — ${p.suggestion.reason}`
      : null,
  }
}
